import React, { useState } from "react";
import {
    Alert,
    AlertTitle,
    Button,
    Chip,
    CircularProgress,
    Typography,
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { FORM3_MIN_SUBJECTS, setForm3SubjectSelectionOpen } from "../../utils/form3SubjectSelection";

/**
 * Form 3/4 class readiness: once ≥8 subjects exist, each new pupil chooses subjects at enrollment.
 */
export default function Form3SubmitToStudentsPanel({
    classId,
    subjectCount = 0,
    selectionOpen = false,
    onUpdated,
    compact = false,
    formLevel = "Form 3",
}) {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const formLabel = formLevel === "Form 4" ? "Form 4" : "Form 3";
    const canSubmit = subjectCount >= FORM3_MIN_SUBJECTS;
    const isOpen = Boolean(selectionOpen);

    const handleSubmit = async () => {
        if (!classId || !canSubmit) return;
        setSaving(true);
        setError("");
        try {
            const result = await setForm3SubjectSelectionOpen(classId, true);
            onUpdated?.(Boolean(result.subjectSelectionOpen));
        } catch (e) {
            setError(
                e.response?.data?.message ||
                    e.response?.data?.error ||
                    e.message ||
                    "Could not mark class ready for enrollment.",
            );
        } finally {
            setSaving(false);
        }
    };

    const handleClose = async () => {
        if (!classId) return;
        setSaving(true);
        setError("");
        try {
            const result = await setForm3SubjectSelectionOpen(classId, false);
            onUpdated?.(Boolean(result.subjectSelectionOpen));
        } catch (e) {
            setError(
                e.response?.data?.message ||
                    e.response?.data?.error ||
                    e.message ||
                    "Could not close subject choice.",
            );
        } finally {
            setSaving(false);
        }
    };

    if (compact && isOpen) {
        return (
            <Chip
                icon={<CheckCircleOutlineIcon />}
                label="Ready — pupils choose subjects at enrollment"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600 }}
            />
        );
    }

    return (
        <Alert
            severity={isOpen ? "success" : canSubmit ? "warning" : "info"}
            sx={{ borderRadius: 2, mb: compact ? 0 : 2 }}
            action={
                isOpen ? (
                    <Button color="inherit" size="small" onClick={handleClose} disabled={saving}>
                        Close for students
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                        onClick={handleSubmit}
                        disabled={!canSubmit || saving}
                        sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                    >
                        Mark ready for enrollment
                    </Button>
                )
            }
        >
            <AlertTitle sx={{ fontWeight: 800 }}>
                {isOpen ? `Ready for ${formLabel} enrollment` : `${formLabel} — subjects for enrollment`}
            </AlertTitle>
            <Typography variant="body2" component="div">
                {isOpen ? (
                    <>
                        This class has at least <strong>{FORM3_MIN_SUBJECTS}</strong> subjects. Every{" "}
                        <strong>new {formLabel} pupil</strong> must choose subjects when enrolled (office or student
                        portal) — the class does not need existing students first.
                    </>
                ) : (
                    <>
                        Add at least <strong>{FORM3_MIN_SUBJECTS}</strong> subjects ({subjectCount}/
                        {FORM3_MIN_SUBJECTS}). Then mark ready so new pupils can pick subjects at enrollment.
                    </>
                )}
            </Typography>
            {error ? (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                    {error}
                </Typography>
            ) : null}
            {!isOpen && !canSubmit ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Add {FORM3_MIN_SUBJECTS - subjectCount} more subject(s), then submit.
                </Typography>
            ) : null}
        </Alert>
    );
}
